import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { API_BASE, getCsrfToken } from '@/api/client'

export interface User {
  id: number
  email: string
  role: 'reader' | 'editor' | 'admin'
  is_active: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isLoading = ref(true)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)
  const isReader = computed(() => user.value?.role === 'reader')
  const isEditor = computed(() => user.value?.role === 'editor' || user.value?.role === 'admin')
  const isAdmin = computed(() => user.value?.role === 'admin')
  const canCreateManualPost = computed(() => isEditor.value)
  const canValidatePosts = computed(() => isEditor.value)
  const canEditSources = computed(() => isEditor.value)
  const canManageUsers = computed(() => isAdmin.value)

  async function init() {
    isLoading.value = true
    error.value = null
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      const { data } = await axios.get(`${API_BASE}/auth/users/me`, {
        withCredentials: true,
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      user.value = data
    } catch (err: any) {
      if (err.response?.status === 401) {
        user.value = null
      } else if (err.name === 'AbortError') {
        error.value = 'Timeout fetching user'
        user.value = null
      } else {
        error.value = err.message
        user.value = null
      }
    } finally {
      isLoading.value = false
    }
  }

  async function login(email: string, password: string) {
    error.value = null
    try {
      await axios.post(
        `${API_BASE}/auth/jwt/login`,
        new URLSearchParams({ username: email, password }),
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }
      )
      await init()
      if (user.value) {
        ElMessage.success(`Вы вошли как ${user.value.email}`)
      }
      return true
    } catch (err: any) {
      if (err.response?.status === 429) {
        error.value = 'Слишком много попыток. Попробуйте позже.'
      } else if (err.response?.status === 400) {
        error.value = 'Неверный email или пароль'
      } else if (err.response?.status >= 500) {
        error.value = 'Сервер временно недоступен'
      } else {
        error.value = 'Не удалось подключиться к серверу'
      }
      return false
    }
  }

  async function logout() {
    try {
      const csrf = getCsrfToken()
      await axios.post(`${API_BASE}/auth/jwt/logout`, {}, {
        withCredentials: true,
        headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
      })
    } catch (err) {
      // Ignore logout errors
    } finally {
      user.value = null
      ElMessage.info('Вы вышли из системы')
      window.location.href = '/login'
    }
  }

  return {
    user,
    isLoading,
    error,
    isAuthenticated,
    isReader,
    isEditor,
    isAdmin,
    canCreateManualPost,
    canValidatePosts,
    canEditSources,
    canManageUsers,
    init,
    login,
    logout,
  }
})
